import { Elements, useElements, useStripe } from "@stripe/react-stripe-js"
// The pure entry: the main one adds the js.stripe.com script as a side effect of the import, so
// Stripe would read this page's URL (an onboarding token, or a Discord code and state) at once.
import { loadStripe } from "@stripe/stripe-js/pure"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
  AdminRegistrationContext,
  AdminRegistrationResponse,
  AdminSession,
} from "@ternary/contracts"
import {
  Alert,
  Avatar,
  Button,
  Field,
  FormWizard,
  Input,
  type WizardErrors,
  type WizardStep,
  type WizardStepContext,
} from "@ternary/ui"
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import {
  confirmAdminPayment,
  confirmAdminPaymentSettled,
  createAdminRegistrationSetupIntent,
  loadAdminRegistrationAuthorize,
  loadAdminRegistrationContext,
  loadAdminRegistrationCoupon,
  registerAdmin,
  requestAdminRegistrationEmailCode,
  verifyAdminRegistrationEmail,
} from "../api.js"
import {
  CardField,
  cardPaymentMethod,
  formatMoney,
  OrderSummary,
  PASSWORD_MESSAGE,
  PlanField,
  passwordErrors,
  period,
  SetupPage,
  useErrorsUntilEdited,
} from "../components/setup-page.js"

/** A message page: the heading, what went wrong or is happening, then its actions. */
function Notice({ children, title }: { children: ReactNode; title: string }) {
  return (
    <SetupPage>
      <div className="admin-setup-panel">
        <h1 className="tny-wizard-title">{title}</h1>
        {children}
      </div>
    </SetupPage>
  )
}

export function AdminRegisterStartPage() {
  const [searchParams] = useSearchParams()
  const configurationError = searchParams.get("configuration")
  const authorization = useQuery({
    enabled: !configurationError,
    queryFn: loadAdminRegistrationAuthorize,
    queryKey: ["admin-registration-authorization"],
  })

  useEffect(() => {
    if (authorization.data?.url) {
      window.location.assign(authorization.data.url)
    }
  }, [authorization.data])

  // The API's own words never show. Only Ternary can fix a platform without Discord configured, so
  // that failure offers no retry.
  const failed = !configurationError && authorization.isError && !authorization.isFetching

  return (
    <Notice title="Connect your Discord server">
      <p className="admin-note">
        Authorize Ternary to create your business dashboard and connect it to your community.
      </p>
      {configurationError ? (
        <Alert
          action={
            <a href="https://docs.ternarydev.com/" rel="noreferrer" target="_blank">
              Ternary documentation
              <span className="tny-visually-hidden"> (opens in a new tab)</span>
            </a>
          }
          tone="danger"
        >
          Sign-up is unavailable right now.
        </Alert>
      ) : failed ? (
        <Alert
          action={
            <Button onClick={() => void authorization.refetch()} size="sm" variant="secondary">
              Try again
            </Button>
          }
          tone="danger"
        >
          We couldn't open Discord. Check your connection and try again.
        </Alert>
      ) : null}
      <p className="admin-note" role="status">
        {configurationError || failed ? "" : "Opening Discord authorization…"}
      </p>
      <Link className="admin-setup-link" to="/">
        Return to sign in
      </Link>
    </Notice>
  )
}

type RegistrationDraft = { password: string; confirmPassword: string }

function AccountStep({
  context,
  draft,
  errors,
  setDraft,
}: WizardStepContext<RegistrationDraft> & { context: AdminRegistrationContext }) {
  const shown = useErrorsUntilEdited(errors, draft)
  return (
    <>
      <ul aria-label="Discord" className="admin-identities">
        <li className="admin-identity">
          <Avatar name={context.user.username} src={context.user.avatarUrl} />
          <div>
            <p className="admin-identity-name">{context.user.email}</p>
            <p className="admin-note">@{context.user.username}</p>
          </div>
        </li>
        <li className="admin-identity">
          <Avatar name={context.guild.name} src={context.guild.iconUrl} />
          <div>
            <p className="admin-identity-name">{context.guild.name}</p>
            <p className="admin-note">Discord server</p>
          </div>
        </li>
      </ul>
      <div className="admin-setup-fields">
        <Field
          error={shown.password}
          hint={shown.password ? undefined : PASSWORD_MESSAGE}
          label="Password"
          required
        >
          <Input
            autoComplete="new-password"
            onChange={(event) => setDraft({ password: event.currentTarget.value })}
            type="password"
            value={draft.password}
          />
        </Field>
        <Field error={shown.confirmPassword} label="Confirm password" required>
          <Input
            autoComplete="new-password"
            onChange={(event) => setDraft({ confirmPassword: event.currentTarget.value })}
            type="password"
            value={draft.confirmPassword}
          />
        </Field>
      </div>
    </>
  )
}

/** admin-api's words for a guild that already has a business (`communityAlreadyRegistered`). */
const SERVER_REGISTERED = "This community is already registered."
/** admin-api's words for an email that already has an account. */
const EMAIL_TAKEN = "Account with email already exists."
/**
 * admin-api's words for a registration context past its 30 minutes (or not one): Back and Pay can
 * never revive it, so the way out is a new Discord authorization. It is checked before any charge.
 */
const SESSION_EXPIRED = "Registration session is invalid or expired."

/**
 * A registration error, from Payment or from Discord's return, as a danger Alert. A server another
 * business registered already offers the way to sign in and the way back to Discord to choose
 * another; an email with an account already offers Sign in. Any other message keeps its words.
 * `restart` adds "Restart registration", for the page Discord returns to, where nothing is left
 * to retry. On Payment, Pay stays as it is (R3).
 */
function RegistrationError({ message, restart = false }: { message: string; restart?: boolean }) {
  const restartLink = restart ? <Link to="/register">Restart registration</Link> : null
  if (message === SERVER_REGISTERED) {
    return (
      <Alert
        action={
          <>
            <Link to="/">Sign in</Link>
            <Link to="/register">Choose another server</Link>
          </>
        }
        tone="danger"
      >
        This Discord server is already registered.
      </Alert>
    )
  }
  if (message === EMAIL_TAKEN) {
    return (
      <Alert
        action={
          <>
            <Link to="/">Sign in</Link>
            {restartLink}
          </>
        }
        tone="danger"
      >
        That email already has a Ternary account.
      </Alert>
    )
  }
  if (message === SESSION_EXPIRED) {
    return (
      <Alert action={<Link to="/register">Restart registration</Link>} tone="danger">
        This sign-up expired after 30 minutes. Restart it to authorize Discord again; nothing was
        charged.
      </Alert>
    )
  }
  return (
    <Alert action={restartLink} tone="danger">
      {message}
    </Alert>
  )
}

/**
 * The code and Apply: a coupon, or (P5) a Stripe promotion code. Payment's hold on the code clears
 * as soon as it changes.
 */
function CouponField({
  applied,
  applying,
  code,
  error,
  errors,
  onApply,
  onChange,
}: {
  /** The code in the field is the applied coupon, so nothing holds it. */
  applied: boolean
  applying: boolean
  code: string
  /** The coupon lookup's error, such as "Coupon not found.", or "". */
  error: string
  /** Payment's errors, which hold the step while a typed code is not applied. */
  errors: WizardErrors
  onApply(): void
  onChange(code: string): void
}) {
  const shown = useErrorsUntilEdited(errors, { coupon: code })
  return (
    <div className="admin-coupon-row">
      <Field error={error || (applied ? undefined : shown.coupon)} label="Promo or coupon code">
        <Input
          autoComplete="off"
          onChange={(event) => onChange(event.currentTarget.value)}
          onKeyDown={(event) => {
            // Enter applies the coupon. It must never submit the form, which pays.
            if (event.key === "Enter") {
              event.preventDefault()
              onApply()
            }
          }}
          spellCheck={false}
          value={code}
        />
      </Field>
      <Button disabled={!code.trim()} loading={applying} onClick={onApply} variant="secondary">
        Apply
      </Button>
    </div>
  )
}

function RegistrationWizard({ context }: { context: AdminRegistrationContext }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const stripe = useStripe()
  const elements = useElements()
  const [couponCode, setCouponCode] = useState("")
  const [couponError, setCouponError] = useState("")
  // P2: with plans to choose from, the chosen one; a free one takes no card and asks Stripe nothing.
  const plans = context.billing.plans
  const [planId, setPlanId] = useState<string | null>(null)
  const plan = plans?.find((candidate) => candidate.priceId === planId) ?? null
  const free = plan?.tier === "free"
  const paid = plans ? plan !== null && !free : true
  // P4: a registration whose first payment waits on the bank's check (3D Secure). Pay then only
  // finishes that check, so the business is never registered twice.
  const pendingPayment = useRef<{
    live: boolean
    onboardingUrl: string
    session: AdminSession
  } | null>(null)
  const setupQuery = {
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      createAdminRegistrationSetupIntent(context.contextToken, { signal }),
    queryKey: ["admin-registration-setup-intent", context.contextToken],
    retry: false,
  }
  const setupIntent = useQuery({ ...setupQuery, enabled: paid })
  const coupon = useMutation({
    mutationFn: loadAdminRegistrationCoupon,
    onError(error) {
      setCouponError(error instanceof Error ? error.message : "Coupon not found.")
    },
    onMutate() {
      setCouponError("")
    },
  })

  // The chosen plan's price, or the configured one, in its own currency and interval (G-6).
  const { amountMinor, currency, interval: unit, intervalCount } = plan ?? context.billing
  const interval = period(unit, intervalCount)
  const typed = couponCode.trim()
  // The coupon counts only while the field still holds its code: cleared, it is not sent.
  const applied =
    coupon.data && typed.toUpperCase() === coupon.data.code.toUpperCase() ? coupon.data : null
  const discountMinor = applied
    ? applied.percentOff !== null
      ? Math.round(amountMinor * (applied.percentOff / 100))
      : Math.min(amountMinor, applied.amountOffMinor ?? 0)
    : 0
  const totalMinor = Math.max(0, amountMinor - discountMinor)
  const money = (minor: number) => formatMoney(minor, currency)
  const off = applied
    ? applied.percentOff !== null
      ? `${applied.percentOff}%`
      : money(applied.amountOffMinor ?? 0)
    : ""

  function applyCoupon() {
    if (typed) coupon.mutate(typed)
  }

  /** What holds Payment: the card setup, or a coupon typed but not applied (or still applying). */
  function paymentErrors(): WizardErrors {
    if (plans && !plan) return { plan: "Choose a plan." }
    // Basic takes no card and no coupon, so nothing holds it.
    if (free) return {}
    const errors: WizardErrors = {}
    if (coupon.isPending || (typed && !applied)) errors.coupon = "Apply the coupon or clear it."
    // Only a setup that failed to load holds Payment, and it is asked for again. One still loading
    // holds nothing, because Pay waits for it.
    if (!setupIntent.isError || setupIntent.isFetching) return errors
    void setupIntent.refetch()
    const message = setupIntent.error instanceof Error ? setupIntent.error.message : ""
    return { ...errors, card: message || "Card entry failed." }
  }

  /** Signs the new owner in and opens the onboarding link (R27), or the dashboard. */
  function enter(session: AdminSession, onboardingUrl: string | undefined) {
    queryClient.clear()
    queryClient.setQueryData(["admin-session"], { authenticated: true, session })
    navigate(onboardingUrl ?? "/dashboard", { replace: true })
  }

  /**
   * P4: Stripe.js finishes the bank's check (the fixture's has none to show), then the confirm
   * read activates the business. A failure keeps it for the next Pay.
   */
  async function finishFirstPayment() {
    const pending = pendingPayment.current
    if (!pending) return
    // L2: the server first; Stripe.js runs only for the check it hands back, then the confirm
    // read is asked again while the bank's answer is on its way.
    let status = await confirmAdminPayment()
    if (status.paymentAction && pending.live) {
      if (!stripe) throw new Error("Secure card entry is still loading.")
      const done = await stripe.confirmCardPayment(status.paymentAction.clientSecret)
      if (done.error)
        throw new Error(done.error.message ?? "Your bank did not confirm the payment.")
      status = await confirmAdminPaymentSettled()
    }
    if (!status.active) {
      throw new Error(
        status.restartable
          ? "This payment can no longer be finished. Start your plan again from Account."
          : "Your bank has not confirmed the payment yet. Try again.",
      )
    }
    pendingPayment.current = null
    enter(pending.session, pending.onboardingUrl)
  }

  async function register(draft: RegistrationDraft) {
    if (pendingPayment.current) {
      await finishFirstPayment()
      return
    }
    if (free && plan) {
      const result = await registerAdmin({
        confirmPassword: draft.confirmPassword,
        contextToken: context.contextToken,
        coupon: null,
        password: draft.password,
        priceId: plan.priceId,
      })
      enter(result.session, result.onboardingUrl)
      return
    }
    // The setup loaded, or the one still loading, such as the fresh one after a refused payment.
    const intent = await queryClient.fetchQuery(setupQuery)
    const paymentMethodId = await cardPaymentMethod(intent, stripe, elements)
    let result: AdminRegistrationResponse
    try {
      result = await registerAdmin({
        confirmPassword: draft.confirmPassword,
        contextToken: context.contextToken,
        coupon: applied?.code ?? null,
        password: draft.password,
        paymentMethodId,
        ...(plan ? { priceId: plan.priceId } : {}),
      })
    } catch (error) {
      // Stripe confirms a setup intent once, so a retry needs a fresh one.
      if (!intent.fixturePaymentMethodId) void setupIntent.refetch()
      throw error
    }
    if (result.paymentAction) {
      pendingPayment.current = {
        live: !intent.fixturePaymentMethodId,
        onboardingUrl: result.onboardingUrl ?? "/dashboard",
        session: result.session,
      }
      await finishFirstPayment()
      return
    }
    // R27: the onboarding link finishes what registration did not cover, Tracking included.
    enter(result.session, result.onboardingUrl)
  }

  const steps: WizardStep<RegistrationDraft>[] = [
    {
      id: "account",
      title: "Account",
      description: "Your Discord account and server. Choose the password you will sign in with.",
      validate: passwordErrors,
      render: (step) => <AccountStep context={context} {...step} />,
    },
    {
      id: "payment",
      title: "Payment",
      validate: paymentErrors,
      render: ({ errors }) => (
        <>
          {plans ? (
            <PlanField
              error={errors.plan}
              onChange={(priceId) => setPlanId(priceId)}
              plans={plans}
              value={planId}
            />
          ) : null}
          {free ? (
            <OrderSummary
              rows={[["Ternary Basic", "Free"]]}
              total={[`Total (${currency})`, "Free"]}
            />
          ) : plans && !plan ? null : (
            <>
              <CouponField
                applied={applied !== null}
                applying={coupon.isPending}
                code={couponCode}
                error={couponError}
                errors={errors}
                onApply={applyCoupon}
                onChange={(code) => {
                  setCouponCode(code)
                  setCouponError("")
                }}
              />
              {/* Mounted empty, so the confirmation is announced as it fills (it takes no gap until then). */}
              <div className="admin-note" role="status">
                {applied ? (
                  <Alert role="none" tone="success">{`${applied.code} applied: ${off} off.`}</Alert>
                ) : null}
              </div>
              <CardField error={errors.card} publishableKey={context.billing.publishableKey} />
              <OrderSummary
                rows={[
                  ["Ternary Business License", `${money(amountMinor)}/${interval}`],
                  ...(applied
                    ? ([[`Coupon ${applied.code}`, `−${money(discountMinor)}`]] as [
                        string,
                        string,
                      ][])
                    : []),
                ]}
                total={[`Total (${currency})`, money(totalMinor)]}
              />
            </>
          )}
        </>
      ),
    },
  ]

  return (
    <FormWizard
      initialDraft={{ confirmPassword: "", password: "" }}
      onSubmit={register}
      renderError={(message) => <RegistrationError message={message} />}
      steps={steps}
      submitLabel={
        paid ? `Pay ${money(totalMinor)}/${interval} and create account` : "Create account"
      }
      title="Create your Ternary account"
    />
  )
}

/**
 * G-8: Discord gave no email, so the owner adds one before the account step: Send code mails a
 * 6-digit code there, and only that code goes on, with a context that now carries the email. A
 * refusal shows in an Alert above the fields.
 */
function EmailStep({
  context,
  onVerified,
}: {
  context: AdminRegistrationContext
  onVerified(verified: AdminRegistrationContext): void
}) {
  const [email, setEmail] = useState("")
  const [code, setCode] = useState("")
  const send = useMutation({
    mutationFn: () => requestAdminRegistrationEmailCode(context.contextToken, email.trim()),
  })
  const verify = useMutation({
    mutationFn: (codeToken: string) =>
      verifyAdminRegistrationEmail({
        code: code.trim(),
        codeToken,
        contextToken: context.contextToken,
      }),
    onSuccess: onVerified,
  })
  const failure = verify.error ?? send.error
  const sent = send.data
  return (
    <div className="admin-setup-panel">
      <h1 className="tny-wizard-title">Confirm your email</h1>
      <p className="admin-note">
        Your Discord account has no email, so add the one your Ternary account will use.
      </p>
      {failure ? (
        <Alert tone="danger">
          {failure instanceof Error ? failure.message : "Something went wrong. Try again."}
        </Alert>
      ) : null}
      <form
        className="admin-setup-fields"
        onSubmit={(event) => {
          event.preventDefault()
          if (sent) verify.mutate(sent.codeToken)
          else send.mutate()
        }}
      >
        <Field label="Email" required>
          <Input
            autoComplete="email"
            onChange={(event) => {
              setEmail(event.currentTarget.value)
              // A changed address needs its own code.
              if (sent) send.reset()
              verify.reset()
            }}
            readOnly={send.isPending}
            type="email"
            value={email}
          />
        </Field>
        {sent ? (
          <Field
            hint={`We sent a 6-digit code to ${email.trim()}. It works for ${sent.expiresInMinutes} minutes.`}
            label="Code"
            required
          >
            <Input
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => setCode(event.currentTarget.value)}
              value={code}
            />
          </Field>
        ) : null}
        <div className="admin-setup-actions">
          {sent ? (
            <>
              <Button loading={verify.isPending} type="submit">
                Confirm email
              </Button>
              <Button
                loading={send.isPending}
                onClick={() => {
                  setCode("")
                  verify.reset()
                  send.mutate()
                }}
                variant="secondary"
              >
                Send a new code
              </Button>
            </>
          ) : (
            <Button disabled={!email.trim()} loading={send.isPending} type="submit">
              Send code
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}

/** The wizard, after Confirm your email when Discord gave no email. */
function Registration({ context }: { context: AdminRegistrationContext }) {
  const [verified, setVerified] = useState<AdminRegistrationContext | null>(null)
  const current = verified ?? context
  return current.user.email ? (
    <RegistrationWizard context={current} />
  ) : (
    <EmailStep context={current} onVerified={setVerified} />
  )
}

export function AdminRegisterConfirmPage() {
  const [searchParams] = useSearchParams()
  const code = searchParams.get("code") ?? ""
  const guildId = searchParams.get("guild_id") ?? ""
  const state = searchParams.get("state") ?? ""
  const context = useQuery({
    enabled: Boolean(code && guildId),
    queryFn: ({ signal }) => loadAdminRegistrationContext({ code, guildId, state }, { signal }),
    queryKey: ["admin-registration-context", code, guildId, state],
    // Discord's code and the state are spent by the first try, so any second request (a retry,
    // a reconnect, a focus, a remount) only ever answers "invalid" and hides the first answer.
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  })
  const publishableKey = context.data?.billing.publishableKey
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  )

  if (!code || !guildId) {
    return (
      <Notice title="Registration could not continue">
        <RegistrationError message="Discord authorization details are missing." restart />
      </Notice>
    )
  }
  if (context.data) {
    return (
      <SetupPage>
        <Elements stripe={stripePromise}>
          <Registration context={context.data} />
        </Elements>
      </SetupPage>
    )
  }
  if (context.isError) {
    return (
      <Notice title="Registration could not continue">
        <RegistrationError
          message={
            context.error instanceof Error ? context.error.message : "Discord authorization failed."
          }
          restart
        />
      </Notice>
    )
  }
  return (
    <SetupPage>
      <p aria-busy="true" className="admin-note" role="status">
        Loading registration…
      </p>
    </SetupPage>
  )
}
